import { useEffect, useMemo, useRef, useState } from "react";
import {
    Button,
    Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { UUID } from "crypto";
import { RootState } from "@store/store";
import { DialogProps, useDialogs } from "@toolpad/core/useDialogs";

import { FinancialCompany } from "@interfaces/catalogs";
import { DialogoBaseProps } from "@interfaces/components";
import { useLazyGetCompaniesQuery } from "@services/api/companies";
import { setFilterListByCompanies, setFilterListByFinancials } from "@store/slices/sessionSlice";
// import { CustomMultipleSelect } from "@components/CustomMultipleSelect";
import { Notificacion } from "./Notificacion";

export const CambiarVistaCompania = (props: DialogProps<DialogoBaseProps>) => {
    const dialogs = useDialogs();
    const dispatch = useDispatch();
    const hasFetched = useRef(false);

    const [financialCoSelected, setFinancialCoSelected] = useState<
        Array<Pick<FinancialCompany, "id" | "name" | "code">>
    >([]);

    // const [allCompaniesChecked, setAllCompaniesChecked] = useState<boolean>(true);
    // const [allFinancialsChecked, setAllFinancialsChecked] = useState<boolean>(true);

    const [values, setValues] = useState<Array<UUID>>([]);
    const [companies, setCompanies] = useState<Array<{ id: UUID; name: string; display_name: string }>>([]);

    const companiesUserAssigned = useSelector((state: RootState) => state.session.companies);
    const companiesFilters = useSelector((state: RootState) => state.session.filters.shared_filters.companies);

    const financialUserAssigned = useSelector((state: RootState) => state.session.financial_inc);
    const financialsFilters = useSelector((state: RootState) => state.session.filters.shared_filters.financials);

    const [triggerGetCompanies] = useLazyGetCompaniesQuery();

    const allCompaniesChecked = useMemo(() => {
        return values.length === companies.length;
    }, [values]);

    const allFinancialsChecked = useMemo(() => {
        return financialCoSelected.length === financialUserAssigned.length;
    }, [financialCoSelected]);

    const handleOnCompaniesChanged = async (_: UUID, selected: Array<any>) => {
        setValues(selected);

        let financialsTmp: Array<Pick<FinancialCompany, "id" | "name" | "code">> = [];

        if (selected.includes(companies.find((t) => t.name === "PYMIO"))) {
            financialsTmp = financialCoSelected;
        }

        if (selected.length > 0) {
            let financialsRelated: Array<Pick<FinancialCompany, "id" | "name" | "code">> = [];
            selected.forEach((companyId) => {
                if (companiesUserAssigned.find((t) => t.id === companyId && t.name === "PYMIO")) {
                } else {
                    financialUserAssigned
                        .filter((f) => f.company_id === companyId)
                        .forEach((financial: any) =>
                            financialsRelated.push({
                                id: financial.id,
                                name: financial.name,
                                code: financial.code,
                            }),
                        );
                }
            });

            setFinancialCoSelected(
                [...financialsTmp, ...financialsRelated].filter(
                    (financial, idx, self) => idx === self.findIndex((c) => c.id === financial.id),
                ),
            );
        }
    };

    const handleOnViewChangeClicked = async () => {
        let selectedCompanies = [];
        if (values.includes("0-0-0-0-0")) {
            selectedCompanies = companies;
        } else {
            selectedCompanies = companies.filter((company) => values.includes(company.id) === true);
        }

        dispatch(setFilterListByCompanies(selectedCompanies));
        dispatch(setFilterListByFinancials(financialCoSelected === undefined ? [] : financialCoSelected));
        // if (selectedCompanies.find((t) => t.name === "PYMIO")) {
        //     dispatch(setFilterListByFinancials(financialCoSelected === undefined ? [] : financialCoSelected));
        // } else {
        //     dispatch(setFilterListByFinancials([]));
        // }
        props.onClose();
    };

    const loadInfo = async () => {
        if (companiesUserAssigned.length === 0) {
            const companies = await triggerGetCompanies().unwrap();
            setCompanies(
                companies.map((company) => ({
                    id: company.id,
                    name: company.display_name,
                    display_name: company.display_name,
                })),
            );
        } else {
            setCompanies(
                companiesUserAssigned.map((company) => ({
                    id: company.id,
                    name: company.name,
                    display_name: company.display_name,
                })),
            );
        }

        setValues(companiesFilters.map((t) => t.id));

        if (financialsFilters) {
            setFinancialCoSelected(financialsFilters);
        }
    };

    useEffect(() => {
        if (hasFetched.current) return;

        loadInfo();

        hasFetched.current = true;
    }, [hasFetched]);

    return (
        <Dialog fullWidth={props.payload.fullWidth} maxWidth={props.payload.maxWidth} open={props.open}>
            <DialogTitle>
                <Typography variant="body1" color="main3" fontWeight={600}>
                    Seleccione la(s) compañia(s) y/o financiera(s):
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ px: 4 }}>
                <Divider>Compañias</Divider>
                <Grid container>
                    {companies.map((company) => (
                        <Grid size={3} key={company.id}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        value={company.id}
                                        checked={values.find((c) => c === company.id) !== undefined}
                                        onChange={async (event, checked) => {
                                            const companyId = event.target.value;
                                            const staging = checked
                                                ? values.concat([company.id])
                                                : values.filter((o) => o !== companyId);
                                            setValues(staging);

                                            if (companies.find((c) => c.id === companyId)?.name === "PYMIO") {
                                                const query = checked
                                                    ? financialCoSelected.length !== financialUserAssigned.length
                                                    : financialCoSelected.length !== 0;
                                                if (query) {
                                                    const response = await dialogs.open(Notificacion, {
                                                        type: "CONFIRM",
                                                        text: `¿Quiere que también se ${checked ? "" : "des"}marquen todas las financieras?`,
                                                    });

                                                    if (response === false) return;

                                                    setFinancialCoSelected(checked ? financialUserAssigned : []);
                                                }
                                            }

                                            let financialRelated = financialUserAssigned.filter(
                                                (f) => f.company_id === companyId,
                                            );

                                            financialRelated = checked
                                                ? financialRelated.filter(
                                                      (t) =>
                                                          financialCoSelected.find((c) => c.id === t.id) === undefined,
                                                  )
                                                : financialRelated.filter((t) =>
                                                      financialCoSelected.find((c) => c.id === t.id),
                                                  );

                                            if (financialRelated.length !== 0) {
                                                const response = await dialogs.open(Notificacion, {
                                                    type: "CONFIRM",
                                                    text: `La compañia seleccionada tiene asociada una financiera, ¿También quiere que sea ${checked ? "seleccionada" : "deseleccionada"}?`,
                                                });
                                                if (response === false) return;

                                                setFinancialCoSelected((oldValues) =>
                                                    checked
                                                        ? oldValues.concat(financialRelated)
                                                        : oldValues.filter(
                                                              (o) =>
                                                                  financialRelated.map((f) => f.id).includes(o.id) ===
                                                                  false,
                                                          ),
                                                );
                                            }
                                        }}
                                    />
                                }
                                label={company.display_name}
                            />
                        </Grid>
                    ))}

                    <Grid container size={12} justifyContent={"end"}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={allCompaniesChecked}
                                    onChange={(_, checked) => {
                                        setValues(checked ? companies.map((c) => c.id) : []);

                                        if (companies.find((c) => c.name === "PYMIO")) {
                                            setFinancialCoSelected(checked ? financialUserAssigned : []);
                                        }
                                    }}
                                />
                            }
                            label={allCompaniesChecked ? "Deseleccionar todos" : "Seleccionar todos"}
                        />
                    </Grid>
                </Grid>
                <Collapse
                    in={
                        values.includes(companies.find((t) => t.name === "PYMIO")?.id ?? "") ||
                        financialCoSelected.length !== 0
                    }
                >
                    <Divider>Financieras</Divider>
                    <Grid container>
                        {financialUserAssigned.map((financialCo) => (
                            <Grid size={3} key={financialCo.id}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            value={financialCo.id}
                                            checked={
                                                financialCoSelected.find((f) => f.id === financialCo.id) !== undefined
                                            }
                                            onChange={(_, checked) => {
                                                setFinancialCoSelected((oldValues) =>
                                                    checked
                                                        ? oldValues.concat([financialCo])
                                                        : oldValues.filter((f) => f.id !== financialCo.id),
                                                );
                                            }}
                                        />
                                    }
                                    label={financialCo.name}
                                />
                            </Grid>
                        ))}
                        <Grid container size={12} justifyContent={"end"}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            allFinancialsChecked ||
                                            financialUserAssigned.length === financialCoSelected.length
                                        }
                                        onChange={(_, checked) => {
                                            // setAllFinancialsChecked(checked);

                                            setFinancialCoSelected(checked ? financialUserAssigned : []);
                                        }}
                                    />
                                }
                                label={allFinancialsChecked ? "Deseleccionar todos" : "Seleccionar todos"}
                            />
                        </Grid>
                    </Grid>
                </Collapse>
            </DialogContent>
            <DialogActions sx={{ mb: 2, justifyContent: "space-evenly" }}>
                <Button variant="blue_outlined" onClick={() => props.onClose()}>
                    Regresar
                </Button>
                <Button
                    variant="contained"
                    disabled={
                        values.length === 0 ||
                        (values.includes(companies.find((t) => t.name === "PYMIO")?.id ?? "") &&
                            financialCoSelected?.length === 0)
                    }
                    onClick={handleOnViewChangeClicked}
                >
                    Cambiar vista
                </Button>
            </DialogActions>
        </Dialog>
    );
};
